// 🔧 industry_landing_page.js
// ✅ Purpose: Reusable SEO-focused public marketing landing page
// ✅ Features:
// - Uses centralized page config from marketing_pages.js
// - Adds Helmet title, description, keywords, canonical URL, and Open Graph metadata
// - Displays hero, benefits, how-it-works, feature cards, ideal-use cards, FAQ, related solutions, and CTA
// - Keeps all marketing landing pages consistent and SEO-focused

import React from "react";
import { Link } from "react-router-dom";
import { MARKETING_PAGE_LINKS } from "../../constants/marketing_pages";
import SeoHead from "../../components/seo_head";

const IndustryLandingPage = ({ page }) => {
  const faqStructuredData =
    (page.faqs || []).length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: page.faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }
      : null;

  return (
    <>
      <SeoHead
        title={page.seoTitle}
        description={page.seoDescription}
        path={page.path}
        image={page.socialImage}
        keywords={page.seoKeywords}
        structuredData={faqStructuredData}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: page.title, path: page.path },
        ]}
      />

      <section id="top-content" className="container-fluid py-5">
        <div className="container">
          <div className="row align-items-center mb-5">
            <div className="col-lg-8 text-start">
              <div className="text-uppercase text-primary fw-bold mb-2">
                {page.eyebrow}
              </div>

              <h1 className="display-4 fw-bold mb-3">
                {page.title}
              </h1>

              <p className="lead text-muted mb-4">
                {page.subtitle}
              </p>

              <div className="d-flex flex-column flex-md-row gap-2">
                <Link to="/request_event" className="btn btn-primary btn-lg">
                  Request Your Event
                </Link>

                <Link to="/solutions" className="btn btn-outline-secondary btn-lg">
                  View All Solutions
                </Link>
              </div>
            </div>
          </div>

          <div className="white-section p-4 rounded shadow-sm bg-white mb-4">
            <h2 className="h3 mb-3 text-start">
              Why organizers use Let&apos;s Go Vote
            </h2>

            <div className="row g-3">
              {[
                "No app downloads for voters",
                "Simple QR-code access",
                "Mobile-friendly ballots",
                "Admin-controlled results",
                "Flexible voting and event tools",
                "Built for real-world community events",
              ].map((benefit) => (
                <div className="col-md-6 col-lg-4" key={benefit}>
                  <div className="border rounded p-3 h-100 text-start">
                    ✅ {benefit}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="white-section p-4 rounded shadow-sm bg-white mb-4">
            <h2 className="h3 mb-3 text-start">
              How it works
            </h2>

            <div className="row g-3">
              {[
                {
                  step: "1",
                  title: "Request your event",
                  text: "Tell us what kind of event you are running and what tools you need.",
                },
                {
                  step: "2",
                  title: "Set up entries and categories",
                  text: "Create the items, people, pets, artwork, or candidates voters will choose from.",
                },
                {
                  step: "3",
                  title: "Share QR codes",
                  text: "Use QR codes at the event so guests can quickly reach the right ballot or item page.",
                },
                {
                  step: "4",
                  title: "Review results",
                  text: "Use admin tools to review votes, results, leaderboards, and event activity.",
                },
              ].map((item) => (
                <div className="col-md-6 col-lg-3" key={item.step}>
                  <div className="border rounded p-3 h-100 text-start">
                    <div className="fw-bold text-primary mb-2">
                      Step {item.step}
                    </div>

                    <h3 className="h6">
                      {item.title}
                    </h3>

                    <p className="text-muted mb-0">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="white-section p-4 rounded shadow-sm bg-white mb-4">
            <h2 className="h3 mb-3 text-start">
              Built for your event
            </h2>

            <div className="row g-3">
              {(page.features || []).map((feature) => (
                <div className="col-md-6 col-lg-4" key={feature.title}>
                  <div className="border rounded p-3 h-100 text-start">
                    <h3 className="h5">
                      {feature.title}
                    </h3>

                    <p className="text-muted mb-0">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="white-section p-4 rounded shadow-sm bg-white mb-4">
            <h2 className="h3 mb-3 text-start">
              Great for
            </h2>

            <div className="row g-2">
              {(page.idealFor || []).map((item) => (
                <div className="col-md-6" key={item}>
                  <div className="border rounded p-3 text-start">
                    ✅ {item}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {(page.faqs || []).length > 0 && (
            <div className="white-section p-4 rounded shadow-sm bg-white mb-4">
              <h2 className="h3 mb-3 text-start">
                Frequently asked questions
              </h2>

              <div className="accordion" id="marketingFaqAccordion">
                {page.faqs.map((faq, index) => (
                  <div className="accordion-item" key={faq.question}>
                    <h3 className="accordion-header" id={`faq-heading-${index}`}>
                      <button
                        className={`accordion-button ${index === 0 ? "" : "collapsed"}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#faq-collapse-${index}`}
                        aria-expanded={index === 0 ? "true" : "false"}
                        aria-controls={`faq-collapse-${index}`}
                      >
                        {faq.question}
                      </button>
                    </h3>

                    <div
                      id={`faq-collapse-${index}`}
                      className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
                      aria-labelledby={`faq-heading-${index}`}
                      data-bs-parent="#marketingFaqAccordion"
                    >
                      <div className="accordion-body text-start">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(page.relatedPages || []).length > 0 && (
            <div className="white-section p-4 rounded shadow-sm bg-white mb-4">
              <h2 className="h3 mb-3 text-start">
                Related solutions
              </h2>

              <div className="row g-3">
                {page.relatedPages.map((pageKey) => {
                  const relatedPage = MARKETING_PAGE_LINKS[pageKey];

                  if (!relatedPage) return null;

                  return (
                    <div className="col-md-6 col-lg-4" key={pageKey}>
                      <Link
                        to={relatedPage.path}
                        className="border rounded p-3 h-100 d-block text-start text-decoration-none"
                      >
                        {relatedPage.title}
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="alert alert-info text-start">
            <strong>Ready to try Let&apos;s Go Vote?</strong>
            <br />
            Request your event and we&apos;ll help you choose the right setup.

            <div className="mt-3">
              <Link to="/request_event" className="btn btn-primary">
                Request Your Event
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default IndustryLandingPage;
